// Freeze-frame share — long-press / S key freezes the canvas, draws a
// share-card overlay (cycle + date + top signal), and offers PNG via Web
// Share API (mobile-friendly) or clipboard (desktop). The plan-review pass
// promoted this to v1 because it's the highest-leverage "today's mood
// escapes the site" lever — the screenshot a visitor takes is part of the
// marketing channel.

import type { Parameters } from '../lib/parameters-schema';

const LONG_PRESS_MS = 600;

export interface FreezeShare {
  trigger(): Promise<void>;
  destroy(): void;
}

export function bindFreezeShare(canvas: HTMLCanvasElement, getParameters: () => Parameters): FreezeShare {
  let pointerStart = 0;
  let pressed = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const trigger = async (): Promise<void> => {
    try {
      const params = getParameters();
      const blob = await composeShareBlob(canvas, params);
      if (!blob) return;
      const filename = `herminator-${params.cycle}-${params.generated_at.slice(0, 10)}.png`;
      const file = new File([blob], filename, { type: 'image/png' });
      flashOverlay();
      await deliverShare(file, blob);
    } catch (err) {
      console.warn('[herminator] freeze share failed:', err);
    }
  };

  // Long-press
  const onPointerDown = (e: PointerEvent): void => {
    pressed = true;
    pointerStart = performance.now();
    timeoutId = setTimeout(() => {
      if (pressed && performance.now() - pointerStart >= LONG_PRESS_MS) void trigger();
    }, LONG_PRESS_MS);
    // Avoid context menu interfering on touch
    if (e.pointerType === 'touch') canvas.setPointerCapture?.(e.pointerId);
  };
  const onPointerUp = (): void => {
    pressed = false;
    if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
  };
  canvas.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerUp);

  // S key
  const onKeyDown = (e: KeyboardEvent): void => {
    if ((e.key === 's' || e.key === 'S') && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      void trigger();
    }
  };
  document.addEventListener('keydown', onKeyDown);

  return {
    trigger,
    destroy(): void {
      canvas.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      document.removeEventListener('keydown', onKeyDown);
      if (timeoutId) clearTimeout(timeoutId);
    },
  };
}

async function composeShareBlob(canvas: HTMLCanvasElement, params: Parameters): Promise<Blob | null> {
  // Compose: render the live canvas at its current resolution, then overlay a
  // share-card strip at the bottom with cycle/date/top-signal.
  const w = canvas.width;
  const h = canvas.height;
  if (w === 0 || h === 0) return null;

  const off = document.createElement('canvas');
  off.width = w; off.height = h;
  const ctx = off.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(canvas, 0, 0, w, h);

  // Strip: 8% of height, semi-transparent dark bar with palette[0] accent line
  const stripH = Math.max(80, Math.round(h * 0.08));
  const y0 = h - stripH;
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(0, y0, w, stripH);
  ctx.fillStyle = params.palette[0] ?? '#ff00aa';
  ctx.fillRect(0, y0, w, 2);

  // Text
  const padX = Math.round(w * 0.025);
  const fontSize = Math.max(18, Math.round(stripH * 0.32));
  ctx.font = `${fontSize}px ui-monospace, SFMono-Regular, Menlo, monospace`;
  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  const yMid = y0 + stripH / 2;
  ctx.fillText('[hrmntr://]', padX, yMid - fontSize * 0.5);
  ctx.font = `${Math.round(fontSize * 0.6)}px ui-monospace, SFMono-Regular, Menlo, monospace`;
  const meta = `cycle ${String(params.cycle).padStart(3, '0')} · ${params.generated_at.slice(0, 10)}`;
  ctx.globalAlpha = 0.65;
  const metaWidth = ctx.measureText(meta).width;
  ctx.fillText(meta, w - padX - metaWidth, yMid - fontSize * 0.5);
  ctx.globalAlpha = 0.85;
  const top = (params.signal_ticker[0] ?? 'signal: silent').slice(0, 80);
  ctx.fillText(top, padX, yMid + fontSize * 0.45);
  ctx.globalAlpha = 1;

  return await new Promise<Blob | null>((resolve) => {
    off.toBlob((b) => resolve(b), 'image/png', 0.92);
  });
}

async function deliverShare(file: File, blob: Blob): Promise<void> {
  // Web Share API with files (mobile-first)
  const navAny = navigator as unknown as { canShare?: (data: { files: File[] }) => boolean; share?: (data: { files: File[]; title?: string; text?: string }) => Promise<void> };
  if (navAny.canShare && navAny.canShare({ files: [file] }) && typeof navAny.share === 'function') {
    try {
      await navAny.share({ files: [file], title: 'herminator', text: 'today’s mood' });
      return;
    } catch (err) {
      // user cancelled — fall through to clipboard / download
      if ((err as { name?: string })?.name === 'AbortError') return;
    }
  }
  // Clipboard
  if (navigator.clipboard && 'write' in navigator.clipboard && typeof window.ClipboardItem !== 'undefined') {
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      flashHint('frame copied to clipboard');
      return;
    } catch {
      // fall through to download
    }
  }
  // Download fallback
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  flashHint('frame downloaded');
}

function flashOverlay(): void {
  const el = document.createElement('div');
  el.className = 'herm-flash';
  document.body.appendChild(el);
  requestAnimationFrame(() => {
    el.style.opacity = '0';
    setTimeout(() => el.remove(), 420);
  });
  ensureFlashStyles();
}

function flashHint(text: string): void {
  const el = document.createElement('div');
  el.className = 'herm-toast';
  el.textContent = text;
  document.body.appendChild(el);
  setTimeout(() => { el.style.opacity = '0'; }, 1400);
  setTimeout(() => el.remove(), 2000);
  ensureFlashStyles();
}

let flashStyled = false;
function ensureFlashStyles(): void {
  if (flashStyled || typeof document === 'undefined') return;
  const css = `
.herm-flash {
  position: fixed; inset: 0;
  background: rgba(255,255,255,0.85);
  pointer-events: none;
  z-index: 100;
  opacity: 1;
  transition: opacity 380ms ease-out;
}
.herm-toast {
  position: fixed;
  left: 50%;
  bottom: clamp(80px, 10vw, 120px);
  transform: translateX(-50%);
  padding: 0.6em 1.1em;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.85rem;
  letter-spacing: 0.16em;
  background: color-mix(in oklch, var(--herm-pal-c, black) 80%, transparent);
  border: 1px solid var(--herm-stroke, rgba(255,255,255,0.4));
  border-radius: 999px;
  color: var(--herm-fg, white);
  pointer-events: none;
  z-index: 99;
  transition: opacity 500ms ease-out;
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}
@media (prefers-reduced-motion: reduce) {
  .herm-flash { transition-duration: 200ms; }
}
`;
  const s = document.createElement('style');
  s.id = 'herminator-flash-styles';
  s.textContent = css;
  document.head.appendChild(s);
  flashStyled = true;
}
